import { useEffect, useState, useCallback, useMemo, useRef } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useFilterGroup } from "@hooks/useFilterGroup";
import { useNotification } from "@hooks/useNotification";
import { useReduxSelector } from "@hooks/useReduxSelector";
import { useUserPermission } from "@hooks/users";
import {
  advantageTypeActions,
  allergyAction,
  clothesAction,
  foodPreferenceAction,
  genderTypeActions
} from "@store/actions";
import moment from "moment-timezone";
import { useForm } from "react-hook-form";

import { getSpecificsEnterpriseSections } from "@pages/layouts/utils/Permissions.lists";

import { ABSENCE, MALADIE, MAX_ANNUAL_SALARY, VACANCE } from "@helpers/_constants";
import {
  SalaryAndAdvantage,
  EmployerSalary
} from "@services/all.services";
import EmployerService from "@services/employers.service";

import {
  getEmployeeDataForBulkEdit,
  filterEmployesList,
  prepareBulkUpdatePayloads,
  handleFormErrors
} from "../utils/bulkEditHelpers";

const DATE_FORMAT = "YYYY-MM-DD";

export default function useBulkEditList() {
  const { showError, showSuccess, showInfo, showWarning } = useNotification();
  const { t: translate, i18n } = useTranslation([
    "pages.employés.emploi&Salary",
    "message"
  ]);
  const { isGroupIdsMatchFilter } = useFilterGroup();
  const reduxDispatch = useDispatch();
  const advantageType = useReduxSelector(
    ({ enterpriseEmployeData }) => enterpriseEmployeData.advantageType
  );
  const {
    isActiveBankingInformationAndInsuranceAccess,
    canSeeEmployeFolder,
    isNotDeniedEmployer,
    haveAccessFolderEnterprise,
    isAllowedTabs
  } = useUserPermission();
  const useTimeBank = useReduxSelector(
    ({ settings }) => settings.enterprise?.useTimeBank || false
  );
  const employeListBulk = useReduxSelector(
    ({ enterpriseEmployeData }) => enterpriseEmployeData.employeListBulk.list
  );
  const isLoaded = useReduxSelector(
    ({ enterpriseEmployeData }) =>
      enterpriseEmployeData.employeListBulk.isLoaded
  );

  const methods = useForm({
    defaultValues: {
      employees: []
    }
  });

  const { setValue, getValues, watch, setError, resetField } = methods;
  const [allSelected, setAllSelected] = useState(false);
  const [selectedEmployeesCount, setSelectedEmployeesCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [filterData, setFilterData] = useState({
    employes: [],
    emplacements: [],
    groupes: [],
    personalInfo: [],
    jobSalaryHolidays: []
  });
  const [filteredEmployesList, setFilteredEmployesList] = useState([]);
  const hasFiltersApplied = useRef(false);
  const initialLoad = useRef(true);
  const isBulkSelectingRef = useRef(false);

  const globalMaxAdvantageTypes = useMemo(() => {
    const advantageCounts = filteredEmployesList.flatMap((employee) =>
      (employee?.salaryAndAdvantages || []).flatMap(
        (advantage) => (advantage.AdvantageType || []).length
      )
    );
    return Math.max(0, ...advantageCounts);
  }, [filteredEmployesList]);

  const employeeBulkFileAvailableSections = useMemo(() => {
    const tabs = ["DListInfopers", "DListEmploisalary"];
    const availableTabs = tabs.filter(isAllowedTabs);
    return availableTabs.length > 0
      ? getSpecificsEnterpriseSections(availableTabs)?.filter(isAllowedTabs)
      : [];
  }, [isAllowedTabs]);

  const isInfoPersAndEmploiSalaryUnavailable = useMemo(() => {
    return (
      !employeeBulkFileAvailableSections?.some((section) =>
        section.includes("DListInfopers")
      ) &&
      !employeeBulkFileAvailableSections?.some((section) =>
        section.includes("DListEmploisalary")
      )
    );
  }, [employeeBulkFileAvailableSections]);

  const employees = useMemo(() => {
    return getEmployeeDataForBulkEdit(
      filteredEmployesList,
      i18n.language,
      filterData
    );
  }, [filteredEmployesList, i18n.language, filterData]);

  useEffect(() => {
    if (employeListBulk?.length) {
      reduxDispatch(allergyAction.loadData);
      reduxDispatch(clothesAction.loadData);
      reduxDispatch(foodPreferenceAction.loadData);
      reduxDispatch(genderTypeActions.loadData);
    }
  }, [employeListBulk]);

  useEffect(() => {
    if (!employeListBulk?.length) return;
    if (!advantageType.isLoaded) {
      reduxDispatch(advantageTypeActions.loadData);
    } else if (advantageType.isDataOutdated) {
      reduxDispatch(advantageTypeActions.refreshList);
    }
  }, [
    advantageType.isLoaded,
    advantageType.isDataOutdated,
    advantageType.invalidationVersion,
    employeListBulk?.length,
    reduxDispatch
  ]);

  const onFilterClicked = useCallback(
    (newFilterData) => {
      setIsFiltering(true);
      const hasFilters =
        newFilterData?.employes?.length > 0 ||
        newFilterData?.emplacements?.length > 0 ||
        newFilterData?.groupes?.length > 0 ||
        newFilterData?.personalInfo?.length > 0 ||
        newFilterData?.jobSalaryHolidays?.length > 0;

      hasFiltersApplied.current = hasFilters;

      setTimeout(async () => {
        try {
          const filtered = await filterEmployesList(
            employeListBulk.map((emp) => ({ ...emp, id: emp._id })),
            newFilterData,
            isGroupIdsMatchFilter
          );
          setFilteredEmployesList(filtered);
          setFilterData(newFilterData);
        } finally {
          setIsFiltering(false);
        }
      }, 0);
    },
    [employeListBulk, isGroupIdsMatchFilter]
  );

  useEffect(() => {
    if (employeListBulk?.length > 0 && initialLoad.current) {
      setFilteredEmployesList(
        employeListBulk.map((emp) => ({ ...emp, id: emp._id }))
      );
      initialLoad.current = false;
    }
  }, [employeListBulk]);

  useEffect(() => {
    if (!initialLoad.current) {
      const currentValues = getValues("employees");
      setValue(
        "employees",
        employees.map((emp) => ({
          ...emp,
          selected:
            currentValues.find((e) => e?.id === emp?.id)?.selected || false
        }))
      );
    }
  }, [employees, setValue, getValues]);

  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (isBulkSelectingRef.current) {
        return;
      }

      if (name && name !== "employees" && !name.endsWith(".selected")) {
        return;
      }

      const currentEmployees = values?.employees || [];
      const selectedCount = currentEmployees.filter(
        (employee) => employee?.selected
      ).length;

      setSelectedEmployeesCount((prev) =>
        prev === selectedCount ? prev : selectedCount
      );

      if (!currentEmployees.length) {
        setAllSelected(false);
      } else if (selectedCount === currentEmployees.length) {
        setAllSelected(true);
      } else if (selectedCount > 0) {
        setAllSelected(null);
      } else {
        setAllSelected(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const handleSelectAll = useCallback(() => {
    const getValuesEmployees = getValues("employees");
    const hasNoAccessEmployees = !canSeeEmployeFolder && employees.length > 0;
    const hasLockedEmployees = getValuesEmployees.some(
      (e) => !isNotDeniedEmployer(e._id)
    );
    const hasLimitedAccessEmployees = getValuesEmployees.some(
      (e) => !haveAccessFolderEnterprise(e._id)
    );

    const newValue = !allSelected;

    if (newValue) {
      const hasBankingAccessIssue = getValuesEmployees.some(
        (emp) =>
          emp.visibleSections?.jobSalaryHolidays?.includes(
            "bankingAndInsuranceInformation"
          ) && !isActiveBankingInformationAndInsuranceAccess
      );

      if (hasBankingAccessIssue) {
        showWarning(
          "Accès limité aux informations bancaires et d'assurance. Veuillez demander l'accès à votre administrateur"
        );
        return;
      }
    }
    if (hasNoAccessEmployees && newValue) {
      return;
    }

    if (hasLockedEmployees && newValue) {
      showError("L'accès au dossier de certains employés est verrouillé");
      return;
    }
    if (hasLimitedAccessEmployees && newValue) {
      showWarning(
        "Accès limité Veuillez demander l'accès à votre administrateur"
      );
      return;
    }

    const nextSelectedValue =
      hasNoAccessEmployees || hasLockedEmployees || hasLimitedAccessEmployees
        ? false
        : newValue;

    isBulkSelectingRef.current = true;
    try {
      getValuesEmployees.forEach((employee, index) => {
        if (employee?.selected === nextSelectedValue) {
          return;
        }

        setValue(`employees.${index}.selected`, nextSelectedValue, {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: false
        });
      });
    } finally {
      isBulkSelectingRef.current = false;
    }

    setAllSelected(nextSelectedValue);
    setSelectedEmployeesCount(
      nextSelectedValue ? getValuesEmployees.length : 0
    );
  }, [
    allSelected,
    getValues,
    setValue,
    showError,
    showWarning,
    canSeeEmployeFolder,
    isNotDeniedEmployer,
    haveAccessFolderEnterprise,
    isActiveBankingInformationAndInsuranceAccess,
    employees
  ]);

  const onSubmit = async (data) => {
    const selectedEmployees = data.employees.filter((e) => e.selected);

    if (!selectedEmployees.length) {
      setIsSaving(false);
      showError("select_at_least_one", { object: "anEmployee" });
      return false;
    }

    try {
      const getValuesEmployees = getValues("employees");
      const payloads = prepareBulkUpdatePayloads(
        selectedEmployees,
        filterData,
        getValuesEmployees
      );
      if (payloads.holidaysPayload?.length) {
        const holidaysPayload = payloads.holidaysPayload.map((holiday) => {
          const selectedEmployee = selectedEmployees.find(
            (employee) => employee?.id === holiday?._id || employee?._id === holiday?._id
          );
          const oldHoursPerDay = Number(selectedEmployee?.hoursPerDay) || 8;
          const newHoursPerDay = Number(holiday?.holiday?.hoursPerDay) || 8;
          const ratio = oldHoursPerDay / newHoursPerDay;
          const adjustHolidayStorageValue = (value) => {
            if (value === "" || value === null || value === undefined) {
              return value;
            }

            return (Number(value) || 0) * ratio;
          };

          if (selectedEmployee?.holidays?.[VACANCE]?.hourFormat === "hours") {
            holiday.holiday.hoursHoliday = adjustHolidayStorageValue(
              holiday.holiday?.hoursHoliday
            );
            holiday.holidayRemaining.hoursHoliday = adjustHolidayStorageValue(
              holiday.holidayRemaining?.hoursHoliday
            );
          }

          if (selectedEmployee?.holidays?.[ABSENCE]?.hourFormat === "hours") {
            holiday.holiday.hoursMiss = adjustHolidayStorageValue(
              holiday.holiday?.hoursMiss
            );
            holiday.holidayRemaining.hoursMiss = adjustHolidayStorageValue(
              holiday.holidayRemaining?.hoursMiss
            );
          }

          if (selectedEmployee?.holidays?.[MALADIE]?.hourFormat === "hours") {
            holiday.holiday.hoursSick = adjustHolidayStorageValue(
              holiday.holiday?.hoursSick
            );
            holiday.holidayRemaining.hoursSick = adjustHolidayStorageValue(
              holiday.holidayRemaining?.hoursSick
            );
          }

          holiday.nextPeriodDecreaseInDays.hoursHoliday = selectedEmployee?.holidays?.[VACANCE]?.nextPeriodDays + selectedEmployee?.holidays?.[VACANCE]?.nextPeriodHours / newHoursPerDay;
          holiday.nextPeriodDecreaseInDays.hoursMiss = selectedEmployee?.holidays?.[ABSENCE]?.nextPeriodDays + selectedEmployee?.holidays?.[ABSENCE]?.nextPeriodHours / newHoursPerDay;
          holiday.nextPeriodDecreaseInDays.hoursSick = selectedEmployee?.holidays?.[MALADIE]?.nextPeriodDays + selectedEmployee?.holidays?.[MALADIE]?.nextPeriodHours / newHoursPerDay;
          holiday.holidayRemaining = {
            hoursHoliday:
              (holiday.holidayRemaining?.hoursHoliday || 0) -
              (holiday.nextPeriodDecreaseInDays?.hoursHoliday || 0),
            hoursMiss:
              (holiday.holidayRemaining?.hoursMiss || 0) -
              (holiday.nextPeriodDecreaseInDays?.hoursMiss || 0),
            hoursSick:
              (holiday.holidayRemaining?.hoursSick || 0) -
              (holiday.nextPeriodDecreaseInDays?.hoursSick || 0)
          };

          holiday.otherHolidays = holiday.otherHolidays.map((otherH, otherHIndex) => {
            const holidayConfig =
              selectedEmployee?.holidays?.otherHolidays?.find(
                (otherHoliday) => otherHoliday?._id === otherH?.holidayName
              ) || selectedEmployee?.holidays?.otherHolidays?.[otherHIndex];

            if (holidayConfig?.hourFormat === "hours") {
              otherH.hours = adjustHolidayStorageValue(otherH.hours);
              otherH.hoursRemaining = adjustHolidayStorageValue(
                otherH.hoursRemaining
              );
            }

            otherH.nextPeriodDecreaseInDays = otherH.nextPeriodDays + otherH.nextPeriodHours / newHoursPerDay;
            otherH.hoursRemaining =
              (otherH.hoursRemaining || 0) -
              (otherH.nextPeriodDecreaseInDays || 0);

            return otherH;
          });
          return holiday;
        });
        await EmployerSalary.bulkUpdateHolidays(holidaysPayload);
      }
      if (payloads.personalInfoPayload?.length) {
        await EmployerService.bulkUpdatePersonalInfo(
          payloads.personalInfoPayload
        );
      }
      if (payloads.emailPhonePayload?.length) {
        await EmployerService.bulkUpdateEmailsAndPhones(
          payloads.emailPhonePayload
        );
      }
      if (payloads.addressPayload?.length) {
        await EmployerService.bulkUpdateAddress(payloads.addressPayload);
      }
      if (payloads.healthPayload?.length) {
        await EmployerService.bulkUpdateHealth(payloads.healthPayload);
      }
      if (payloads.clothesPayload?.length) {
        await EmployerService.bulkUpdateClothes(payloads.clothesPayload);
      }
      if (payloads.firstContactPayload?.length) {
        await EmployerService.bulkUpdateFirstEmergency(
          payloads.firstContactPayload
        );
      }
      if (payloads.secondContactPayload?.length) {
        await EmployerService.bulkUpdateSecondEmergency(
          payloads.secondContactPayload
        );
      }
      if (payloads.additionalInfo?.length) {
        await EmployerService.bulkUpdateAdditionalInfo(payloads.additionalInfo);
      }
      if (payloads.jobPayload?.length) {
        await EmployerSalary.bulkUpdateJob(payloads.jobPayload);
      }
      if (payloads.securityPayload?.length) {
        await EmployerSalary.bulkUpdateSecurity(payloads.securityPayload);
      }
      if (payloads.bankingInsurancePayload?.length) {
        await EmployerSalary.bulkUpdateBankingInsurance(
          payloads.bankingInsurancePayload
        );
      }
      if (payloads.salaryAndAdvantagesPayload?.length) {
        for (const employeeData of payloads.salaryAndAdvantagesPayload) {
          const { employeeIndex } = employeeData;
          const employee = getValuesEmployees[employeeIndex];

          if (!employee?.selected) continue;

          for (const [advantageIndex, advantage] of (
            employee.salaryAndAdvantages || []
          ).entries()) {
            const {
              emploiCourses: career,
              salaryBegin,
              salaryEnd,
              isInProgress
            } = advantage;
            const salaryValue = advantage?.salary;

            if (
              salaryValue !== undefined &&
              salaryValue !== null &&
              salaryValue !== ""
            ) {
              const parsedSalary = Number(salaryValue);
              if (
                !Number.isFinite(parsedSalary) ||
                parsedSalary > MAX_ANNUAL_SALARY
              ) {
                setError(
                  `employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.salary`,
                  {
                    type: "annualSalaryMax"
                  }
                );
                showError(
                  translate("annual_salary_max_exceeded", { ns: "message" })
                );
                setIsSaving(false);
                return false;
              }
            }

            if (!salaryBegin) {
              setError(
                `employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.salaryBegin`,
                {
                  type: "required"
                }
              );
              showError("field_obligatory");
              setIsSaving(false);
              return false;
            }

            if (career?.dateStart) {
              const isSalaryBeginValid = !career.endDate
                ? moment(salaryBegin).isSameOrAfter(
                  moment(career.dateStart, DATE_FORMAT),
                  "day"
                )
                : moment(salaryBegin).isBetween(
                  moment(career.dateStart, DATE_FORMAT),
                  moment(career.endDate, DATE_FORMAT),
                  "days",
                  "[]"
                );

              if (!isSalaryBeginValid) {
                setError(
                  `employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.salaryBegin`,
                  {
                    type: "isBetweenPeriod"
                  }
                );
                showError(
                  "La période sélectionnée dépasse celle du poste approprié"
                );
                setIsSaving(false);
                return false;
              }
            }

            const isSalaryEndRequired =
              isInProgress || !salaryBegin || career?.status
                ? true
                : !salaryEnd;

            if (
              isSalaryEndRequired &&
              !(isInProgress || !salaryBegin || career?.status)
            ) {
              setError(
                `employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.salaryEnd`,
                {
                  type: "required"
                }
              );
              showError("field_obligatory");
              setIsSaving(false);
              return false;
            }

            if (salaryEnd) {
              const isAfterSalaryBeginValid =
                isInProgress || !salaryBegin || career?.status
                  ? true
                  : moment(salaryEnd).isSameOrAfter(salaryBegin, "day");

              if (!isAfterSalaryBeginValid) {
                setError(
                  `employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.salaryEnd`,
                  {
                    type: "isAfterSalaryBegin"
                  }
                );
                showError("verification_two_object", {
                  object1: "endDate",
                  operator: "superiour",
                  object2: "startDate"
                });
                setIsSaving(false);
                return false;
              }

              if (career?.dateStart) {
                const isBetweenPeriodValid =
                  isInProgress || career?.status
                    ? true
                    : !career.endDate
                      ? moment(salaryEnd).isSameOrAfter(
                        moment(career.dateStart, DATE_FORMAT),
                        "day"
                      )
                      : moment(salaryEnd).isBetween(
                        moment(career.dateStart, DATE_FORMAT),
                        moment(career.endDate, DATE_FORMAT),
                        "days",
                        "[]"
                      );

                if (!isBetweenPeriodValid) {
                  setError(
                    `employees.${employeeIndex}.salaryAndAdvantages.${advantageIndex}.salaryEnd`,
                    {
                      type: "isBetweenPeriod"
                    }
                  );
                  showError(
                    "La période sélectionnée dépasse celle du poste approprié"
                  );
                  setIsSaving(false);
                  return false;
                }
              }
            }
          }
        }

        const response = await SalaryAndAdvantage.bulkUpdateSalaryAndAdvantages(
          payloads.salaryAndAdvantagesPayload
        );
        if (!response?.success) {
          showError(response?.message, { date: response?.date });
          setIsSaving(false);
          return false;
        }
      }

      showSuccess("changes_saved");
      const updatedEmployees = getValues("employees").map((employee) => ({
        ...employee,
        selected: false
      }));
      resetField("employees", {
        defaultValue: updatedEmployees
      });
      setSelectedEmployeesCount(0);
      setAllSelected(false);
      return true;
    } catch (err) {
      console.error(err);
      showError("error");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleError = (errors) => {
    handleFormErrors(errors, showError, showInfo);
    setIsSaving(false);
  };

  return {
    methods,
    isLoaded,
    isFiltering,
    isSaving,
    filterData,
    filteredEmployesList: employees,
    useTimeBank,
    setIsSaving,
    allSelected,
    selectedEmployeesCount,
    handleSelectAll,
    onSubmit,
    handleError,
    onFilterClicked,
    canSeeEmployeFolder,
    isNotDeniedEmployer,
    haveAccessFolderEnterprise,
    isActiveBankingInformationAndInsuranceAccess,
    employeeBulkFileAvailableSections,
    isInfoPersAndEmploiSalaryUnavailable,
    globalMaxAdvantageTypes
  };
}
